const SDK_PACKAGE_NAMES = ["@twilio/flex-sdk", "flex-sdk-js"];

const SDK_INTERNAL_PATH_HINTS = [
    "/dist/npm/",
    "/dist/npm/esm/",
    "/dist/cjs/",
    "/src/modules/",
    "/src/packages/",
    "/src/utils/"
];

const INTERNAL_FRAME_TOKEN = "[flex-sdk]";
const EXTERNAL_FRAME_TOKEN = "[external]";
const REDACTED_TOKEN = "[redacted]";

const SID_PATTERN = /\b[A-Z]{2}[a-f0-9]{32}\b/g;
const PHONE_PATTERN = /\+?\d[\d\s().-]{7,}\d/g;
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

interface ParsedFrame {
    fnName: string;
    filename: string;
    line?: number;
    col?: number;
    isInternal: boolean;
}

export function isInternalFilename(filename: string): boolean {
    if (!filename) return false;
    if (SDK_PACKAGE_NAMES.some((name) => filename.includes(name))) return true;
    return SDK_INTERNAL_PATH_HINTS.some((hint) => filename.includes(hint));
}

export function sanitizeMessage(input: string | undefined): string {
    if (!input) return "";
    const sids: string[] = [];
    let working = input.replace(SID_PATTERN, (sid) => {
        sids.push(sid);
        return `SID${sids.length - 1}`;
    });
    working = working.replace(EMAIL_PATTERN, REDACTED_TOKEN);
    working = working.replace(PHONE_PATTERN, REDACTED_TOKEN);
    working = working.replace(/SID(\d+)/g, (_, idx) => sids[Number(idx)]);
    return working;
}

function parseFrame(line: string): ParsedFrame | null {
    const trimmed = line.trim();
    if (!trimmed.startsWith("at ")) return null;
    const body = trimmed.slice(3);

    const parenMatch = /^(.*?)\s*\((.*?)\)\s*$/.exec(body);
    let fnName: string;
    let location: string;
    if (parenMatch) {
        fnName = parenMatch[1] || "?";
        location = parenMatch[2];
    } else {
        fnName = "?";
        location = body;
    }

    const locMatch = /^(.*?):(\d+):(\d+)$/.exec(location);
    let filename = location;
    let lineno: number | undefined;
    let colno: number | undefined;
    if (locMatch) {
        filename = locMatch[1];
        lineno = Number(locMatch[2]);
        colno = Number(locMatch[3]);
    }

    return {
        fnName,
        filename,
        line: lineno,
        col: colno,
        isInternal: isInternalFilename(filename)
    };
}

function stripInternalPrefix(filename: string): string {
    const noQuery = filename.split("?")[0];
    for (const hint of SDK_INTERNAL_PATH_HINTS) {
        const idx = noQuery.indexOf(hint);
        if (idx >= 0) {
            return `${INTERNAL_FRAME_TOKEN}${noQuery.slice(idx)}`;
        }
    }
    for (const pkg of SDK_PACKAGE_NAMES) {
        const idx = noQuery.indexOf(pkg);
        if (idx >= 0) {
            return `${INTERNAL_FRAME_TOKEN}/${noQuery.slice(idx)}`;
        }
    }
    return INTERNAL_FRAME_TOKEN;
}

export function sanitizeStack(stack: string | undefined, message: string): string {
    const safeHeader = `Error: ${sanitizeMessage(message)}`;
    if (!stack) return safeHeader;

    const lines = stack.split("\n");
    const cleanedFrames: string[] = [];

    for (const line of lines) {
        const frame = parseFrame(line);
        if (!frame) continue;

        if (frame.isInternal) {
            const trimmedFile = stripInternalPrefix(frame.filename);
            const loc =
                frame.line !== undefined && frame.col !== undefined
                    ? `${trimmedFile}:${frame.line}:${frame.col}`
                    : trimmedFile;
            cleanedFrames.push(`    at ${frame.fnName} (${loc})`);
        } else {
            cleanedFrames.push(`    at ${REDACTED_TOKEN} (${EXTERNAL_FRAME_TOKEN})`);
        }
    }

    const externalSuffix = `(${EXTERNAL_FRAME_TOKEN})`;
    const collapsed: string[] = [];
    for (const f of cleanedFrames) {
        const prev = collapsed[collapsed.length - 1];
        if (prev && f.endsWith(externalSuffix) && prev.endsWith(externalSuffix)) {
            continue;
        }
        collapsed.push(f);
    }

    return [safeHeader, ...collapsed].join("\n");
}

export function sanitizeError(error: Error): Error {
    const safeMessage = sanitizeMessage(error.message);
    const safeStack = sanitizeStack(error.stack, error.message);
    const cloned = new Error(safeMessage);
    cloned.name = error.name;
    cloned.stack = safeStack;
    return cloned;
}
